// What the on-page editor and the Layout panel know about resume sections. Titles
// are the standard bar titles the Word template and polish use.
export interface ListSection {
  key: string;
  title: string;
  fields: Array<{ label: string; keys: string[] }>;
}

export const LIST_SECTIONS: ListSection[] = [
  { key: 'AwardsAndHonors', title: 'Awards & Honors', fields: [
    { label: 'Award', keys: ['Award_Name'] }, { label: 'Organization', keys: ['Institution_Organization'] }, { label: 'Year', keys: ['Year'] } ] },
  { key: 'CertificationsAndLicenses', title: 'Certifications & Professional Development', fields: [
    { label: 'Certification', keys: ['Certification_or_License_Name'] }, { label: 'Organization', keys: ['Institution_Organization'] }, { label: 'Year', keys: ['Year', 'Year_Earned'] } ] },
  { key: 'TrainingAndDevelopment', title: 'Training & Development', fields: [
    { label: 'Training', keys: ['Course_Training_Name', 'Training_Name', 'Training', 'Course_Name'] }, { label: 'Organization', keys: ['Institution_Provider', 'Institution_Organization'] }, { label: 'Year', keys: ['Year'] } ] },
  { key: 'Languages', title: 'Languages', fields: [
    { label: 'Language', keys: ['Language'] }, { label: 'Level', keys: ['Proficiency_Level'] } ] },
  { key: 'ProfessionalAffiliations', title: 'Professional Affiliations', fields: [
    { label: 'Role', keys: ['Membership_Role'] }, { label: 'Organization', keys: ['Organization'] }, { label: 'Year', keys: ['Year'] } ] },
  { key: 'Publications', title: 'Publications', fields: [
    { label: 'Title', keys: ['Title_of_Publication'] }, { label: 'Publication', keys: ['Publication_Name'] }, { label: 'Date', keys: ['Date'] } ] },
  { key: 'Presentations', title: 'Presentations', fields: [
    { label: 'Title', keys: ['Title_of_Presentation'] }, { label: 'Event', keys: ['Event_Name'] }, { label: 'Date', keys: ['Date'] } ] },
  { key: 'MilitaryExperience', title: 'Military Experience', fields: [
    { label: 'Rank', keys: ['Rank_Position'] }, { label: 'Branch', keys: ['Branch'] }, { label: 'Dates', keys: ['Service_Dates'] } ] },
  { key: 'VolunteerMemberships', title: 'Volunteer & Memberships', fields: [
    { label: 'Position', keys: ['PositionName'] }, { label: 'Organization', keys: ['InstitutionOrganization'] } ] },
];

const hasItems = (v: unknown) => Array.isArray(v) && v.length > 0;

export const skillsOf = (rows: any, prefix: string): string[] =>
  (rows ?? []).flatMap((r: any) => [1, 2, 3, 4].map((i) => String(r?.[`${prefix}${i}`] ?? '').trim()).filter(Boolean));

/** Skills go back four to a row; the renderer repacks them to the fewest rows. */
export const rowsOf = (skills: string[], prefix: string) => {
  const rows: Array<Record<string, string>> = [];
  for (let i = 0; i < skills.length; i += 4) {
    const row: Record<string, string> = {};
    [0, 1, 2, 3].forEach((k) => { row[`${prefix}${k + 1}`] = skills[i + k] ?? ''; });
    rows.push(row);
  }
  return rows;
};

/** Sections on this resume, in their current order. The summary always stays on top. */
export function sectionTitles(json: any): string[] {
  const titles = ['Skills'];
  if (hasItems(json?.CareerHighlights)) titles.push('Career Highlights');
  titles.push('Professional Experience');
  if (skillsOf(json?.TechnicalSkillRows, 'TechSkill').length) titles.push('Technical Skills');
  for (const s of LIST_SECTIONS) if (hasItems(json?.[s.key])) titles.push(s.title);
  if (hasItems(json?.Education)) titles.push('Education');
  const saved: string[] = json?.ResumeLayout?.sectionOrder ?? [];
  const known = saved.filter((t) => titles.includes(t));
  return [...known, ...titles.filter((t) => !known.includes(t))];
}

export const SUMMARY_TITLE = 'Career Summary';
